/**
 * Company Page Service — Lending Network Module 8
 * API service for company page operations.
 */

import axiosInstance from '../config/axios.config';

const BASE_URL = '/leadflow/company-pages';

export const companyPageService = {
    /**
     * Browse all company pages (directory)
     */
    async getPages({ search = '', perPage = 12, page = 1 } = {}) {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (perPage) params.append('per_page', perPage);
            if (page) params.append('page', page);
            const url = params.toString() ? `${BASE_URL}?${params}` : BASE_URL;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return { success: false, data: null, message: error.response?.data?.message || 'Failed to load company pages' };
        }
    },

    /**
     * Get the company page that matches the logged-in agent's current_company
     */
    async getMyCompanyPage() {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/my-company`);
            return response.data;
        } catch (error) {
            return { success: false, data: null, message: error.response?.data?.message || 'Failed to load company page' };
        }
    },

    /**
     * Get a single company page by slug
     */
    async getPage(slug) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/${slug}`);
            return response.data;
        } catch (error) {
            return { success: false, data: null, message: error.response?.data?.message || 'Failed to load company page' };
        }
    },

    /**
     * Get agents (people) on a company page
     */
    async getPagePeople(slug, { search = '', perPage = 20, page = 1 } = {}) {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (perPage) params.append('per_page', perPage);
            if (page) params.append('page', page);
            const url = params.toString()
                ? `${BASE_URL}/${slug}/people?${params}`
                : `${BASE_URL}/${slug}/people`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return { success: false, data: null, message: error.response?.data?.message || 'Failed to load people' };
        }
    },

    /**
     * Get posts on a company page
     */
    async getPagePosts(slug, { perPage = 10, page = 1 } = {}) {
        try {
            const response = await axiosInstance.get(
                `${BASE_URL}/${slug}/posts?per_page=${perPage}&page=${page}`
            );
            return response.data;
        } catch (error) {
            return { success: false, data: null, message: error.response?.data?.message || 'Failed to load posts' };
        }
    },

    /**
     * Follow or unfollow a company page
     */
    async toggleFollow(slug) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/${slug}/follow`);
            return response.data;
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'Failed to update follow status' };
        }
    },

    /**
     * Create an official company post (editor/super_admin only)
     */
    async createOfficialPost(slug, { content, post_type = 'general', tags = [], media_type = null, media_urls = null }) {
        try {
            const payload = { content, post_type, tags };
            if (media_type && media_urls) { payload.media_type = media_type; payload.media_urls = media_urls; }
            const response = await axiosInstance.post(`${BASE_URL}/${slug}/posts`, payload);
            return response.data;
        } catch (error) {
            const errors = error.response?.data?.errors;
            const firstError = errors ? Object.values(errors)[0]?.[0] : null;
            return { success: false, message: firstError || error.response?.data?.message || 'Failed to create post' };
        }
    },

    /**
     * Get page managers (super_admin/editor only)
     */
    async getManagers(slug) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/${slug}/managers`);
            return response.data;
        } catch (error) {
            return { success: false, data: [], message: error.response?.data?.message || 'Failed to load managers' };
        }
    },

    /**
     * Add a new manager to the page (super_admin only)
     */
    async addManager(slug, userId, role) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/${slug}/managers`, {
                user_id: userId,
                role,
            });
            return response.data;
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'Failed to add manager' };
        }
    },

    /**
     * Update a manager's role (super_admin only)
     */
    async updateManagerRole(slug, managerId, role) {
        try {
            const response = await axiosInstance.put(`${BASE_URL}/${slug}/managers/${managerId}`, { role });
            return response.data;
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'Failed to update role' };
        }
    },

    /**
     * Remove a manager from the page (super_admin only)
     */
    async removeManager(slug, managerId) {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/${slug}/managers/${managerId}`);
            return response.data;
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'Failed to remove manager' };
        }
    },

    async rate(slug, { rating, review = null } = {}) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/${slug}/rate`, { rating, review });
            return response.data;
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'Failed to submit rating' };
        }
    },
};

export default companyPageService;
